import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type HTMLAttributes,
  type PointerEvent,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";

type BorderGlowElement = "div" | "form" | "span" | "button";

type BorderGlowProps = Omit<
  HTMLAttributes<HTMLElement>,
  "as" | "children" | "color"
> & {
  as?: BorderGlowElement;
  /** HTML `type` attribute — forwarded to the DOM when `as="button"`. */
  type?: "button" | "submit" | "reset";
  children?: ReactNode;
  active?: boolean;
  animated?: boolean;
  activeOnHover?: boolean;
  edgeSensitivity?: number;
  glowColor?: string;
  glowRadius?: number;
  glowIntensity?: number;
  coneSpread?: number;
  colors?: string[];
  borderWidth?: number;
  ringMode?: "comet" | "solid";
  /**
   * Push the running comet ring this many px OUTSIDE the host's padding box.
   * Use a value ≥ the host's own border width when the host (e.g. a glass
   * frame) draws its own border, so the comet sits on/just outside the
   * visible edge instead of ~1px inside it. Default 0 (ring on padding box).
   */
  ringOffset?: number;
  animationDuration?: number;
  showGlowHalo?: boolean;
  disabled?: boolean;
};

function parseHsl(hsl: string): { h: number; s: number; l: number } {
  const match = hsl.match(/([\d.]+)\s*([\d.]+)%?\s*([\d.]+)%?/);
  if (!match) return { h: 145, s: 78, l: 55 };
  return {
    h: parseFloat(match[1] ?? "145"),
    s: parseFloat(match[2] ?? "78"),
    l: parseFloat(match[3] ?? "55"),
  };
}

function buildBoxShadow(glowColor: string, intensity: number): string {
  const { h, s, l } = parseHsl(glowColor);
  const base = `${h}deg ${s}% ${l}%`;
  const layers: [number, number, number, number, number, boolean][] = [
    [0, 0, 0, 1, 82, true],
    [0, 0, 1, 0, 56, true],
    [0, 0, 4, 0, 42, true],
    [0, 0, 10, 0, 30, true],
    [0, 0, 18, 1, 18, true],
    [0, 0, 1, 0, 42, false],
    [0, 0, 5, 0, 34, false],
    [0, 0, 14, 1, 22, false],
    [0, 0, 28, 2, 12, false],
  ];
  return layers
    .map(([x, y, blur, spread, alpha, inset]) => {
      const a = Math.min(alpha * intensity, 100);
      return `${inset ? "inset " : ""}${x}px ${y}px ${blur}px ${spread}px hsl(${base} / ${a}%)`;
    })
    .join(", ");
}

const GRADIENT_POSITIONS = [
  "80% 55%",
  "69% 34%",
  "8% 6%",
  "41% 38%",
  "86% 85%",
  "82% 18%",
  "51% 4%",
] as const;
const COLOR_MAP = [0, 1, 2, 0, 1, 2, 1] as const;

function buildMeshGradients(colors: string[]): string[] {
  const gradients: string[] = [];
  for (let i = 0; i < GRADIENT_POSITIONS.length; i += 1) {
    const color = colors[Math.min(COLOR_MAP[i] ?? 0, colors.length - 1)];
    gradients.push(
      `radial-gradient(at ${GRADIENT_POSITIONS[i]}, ${color} 0px, transparent 50%)`,
    );
  }
  gradients.push(`linear-gradient(${colors[0]} 0 100%)`);
  return gradients;
}

function clampOpacity(value: number) {
  return Math.max(0, Math.min(1, value));
}

const BorderGlow = memo(function BorderGlow({
  as = "div",
  children,
  active = false,
  animated = false,
  activeOnHover = true,
  edgeSensitivity = 30,
  glowColor = "145 78 55",
  glowRadius = 26,
  glowIntensity = 0.9,
  coneSpread = 25,
  colors = ["#30D158", "#38BDF8", "#F472B6"],
  borderWidth = 1,
  ringMode = "comet",
  ringOffset = 0,
  animationDuration = 3,
  showGlowHalo = true,
  disabled = false,
  className,
  style,
  onPointerEnter,
  onPointerMove,
  onPointerLeave,
  ...props
}: BorderGlowProps) {
  const rootRef = useRef<HTMLElement | null>(null);
  const [hovered, setHovered] = useState(false);
  const Component = as as ElementType;
  const isRunning = !disabled && animated && active;
  const isVisible = !disabled && (active || hovered);
  const isSolidRing = ringMode === "solid";
  const meshGradients = buildMeshGradients(colors);
  const borderBg = meshGradients.map((gradient) => `${gradient} border-box`);

  const setGlowOpacity = useCallback((border: number, glow: number) => {
    const root = rootRef.current;
    if (!root) return;
    root.style.setProperty(
      "--denker-border-glow-border-opacity",
      String(clampOpacity(border)),
    );
    root.style.setProperty(
      "--denker-border-glow-outer-opacity",
      String(clampOpacity(glow)),
    );
  }, []);

  useEffect(() => {
    if (disabled) {
      setGlowOpacity(0, 0);
      return;
    }
    if (active) {
      setGlowOpacity(1, 0.86);
    } else if (!hovered) {
      setGlowOpacity(0, 0);
    }
  }, [active, disabled, hovered, setGlowOpacity]);

  const updatePointerGlow = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const root = rootRef.current;
      if (!root || disabled || active || !activeOnHover) return;
      const rect = root.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const dx = x - cx;
      const dy = y - cy;
      const kx = dx === 0 ? Infinity : cx / Math.abs(dx);
      const ky = dy === 0 ? Infinity : cy / Math.abs(dy);
      const edgeProximity = Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);
      const degrees = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      const angle = degrees < 0 ? degrees + 360 : degrees;
      const colorSensitivity = edgeSensitivity + 20;
      const borderOpacity =
        (edgeProximity * 100 - colorSensitivity) / (100 - colorSensitivity);
      const outerOpacity =
        (edgeProximity * 100 - edgeSensitivity) / (100 - edgeSensitivity);
      root.style.setProperty("--denker-border-glow-angle", `${angle}deg`);
      setGlowOpacity(borderOpacity, outerOpacity);
    },
    [active, activeOnHover, disabled, edgeSensitivity, setGlowOpacity],
  );

  const handlePointerEnter = (event: PointerEvent<HTMLElement>) => {
    if (activeOnHover && !disabled) setHovered(true);
    updatePointerGlow(event);
    onPointerEnter?.(event);
  };

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    updatePointerGlow(event);
    onPointerMove?.(event);
  };

  const handlePointerLeave = (event: PointerEvent<HTMLElement>) => {
    setHovered(false);
    if (!active) setGlowOpacity(0, 0);
    onPointerLeave?.(event);
  };

  return (
    <Component
      ref={(node: HTMLElement | null) => {
        rootRef.current = node;
      }}
      className={cn(
        "denker-border-glow relative isolate rounded-[inherit]",
        className,
      )}
      style={
        {
          "--denker-border-glow-angle": "45deg",
          "--denker-border-glow-border-opacity": active ? 1 : 0,
          "--denker-border-glow-outer-opacity": active ? 0.86 : 0,
          "--denker-border-glow-speed": `${animationDuration}s`,
          ...style,
        } as CSSProperties
      }
      data-border-glow-visible={isVisible ? "true" : undefined}
      data-border-glow-running={isRunning ? "true" : undefined}
      aria-disabled={as === "button" && disabled ? true : undefined}
      onPointerEnter={handlePointerEnter}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      {...props}
      {...(as === "button" ? { disabled } : null)}
    >
      <div
        className="denker-border-glow-layer pointer-events-none absolute z-0 rounded-[inherit]"
        style={
          {
            inset: `${-ringOffset}px`,
            border: `${borderWidth}px solid transparent`,
            background: borderBg.join(", "),
            maskImage: isSolidRing
              ? [
                  "linear-gradient(#000 0 0)",
                  "linear-gradient(#000 0 0)",
                  "linear-gradient(#000 0 0)",
                ].join(", ")
              : [
                  `conic-gradient(from var(--denker-border-glow-angle) at center, black ${coneSpread}%, transparent ${coneSpread + 15}%, transparent ${100 - coneSpread - 15}%, black ${100 - coneSpread}%)`,
                  "linear-gradient(#000 0 0)",
                  "linear-gradient(#000 0 0)",
                ].join(", "),
            maskClip: "border-box, padding-box, border-box",
            maskComposite: "intersect, exclude, add",
            WebkitMaskImage: isSolidRing
              ? [
                  "linear-gradient(#000 0 0)",
                  "linear-gradient(#000 0 0)",
                  "linear-gradient(#000 0 0)",
                ].join(", ")
              : [
                  `conic-gradient(from var(--denker-border-glow-angle) at center, black ${coneSpread}%, transparent ${coneSpread + 15}%, transparent ${100 - coneSpread - 15}%, black ${100 - coneSpread}%)`,
                  "linear-gradient(#000 0 0)",
                  "linear-gradient(#000 0 0)",
                ].join(", "),
            WebkitMaskClip: "border-box, padding-box, border-box",
            WebkitMaskComposite: "source-in, xor, source-over",
            opacity: "var(--denker-border-glow-border-opacity)",
            transition: isVisible
              ? "opacity 180ms ease-out"
              : "opacity 520ms ease-in-out",
          } as CSSProperties
        }
      />
      {showGlowHalo && (
        <span
          className="denker-border-glow-layer pointer-events-none absolute z-0 rounded-[inherit]"
          style={
            {
              inset: `${-glowRadius}px`,
              maskImage: isSolidRing
                ? "linear-gradient(#000 0 0)"
                : "conic-gradient(from var(--denker-border-glow-angle) at center, black 2.5%, transparent 10%, transparent 90%, black 97.5%)",
              WebkitMaskImage: isSolidRing
                ? "linear-gradient(#000 0 0)"
                : "conic-gradient(from var(--denker-border-glow-angle) at center, black 2.5%, transparent 10%, transparent 90%, black 97.5%)",
              opacity: "var(--denker-border-glow-outer-opacity)",
              mixBlendMode: "plus-lighter",
              transition: isVisible
                ? "opacity 180ms ease-out"
                : "opacity 520ms ease-in-out",
            } as CSSProperties
          }
        >
          <span
            className="absolute rounded-[inherit]"
            style={{
              inset: `${glowRadius}px`,
              boxShadow: buildBoxShadow(glowColor, glowIntensity),
            }}
          />
        </span>
      )}
      {children}
    </Component>
  );
});

export { BorderGlow };
export type { BorderGlowProps };
